/**
 * @fileoverview Label Component
 * 
 * Accessible form label component built on Radix UI primitives.
 * Provides proper accessibility for form inputs.
 * 
 * @module components/Label
 */

import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * Label variant styles configuration
 * 
 * @constant
 */
const labelVariants = cva(
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
);

/**
 * Label component for form inputs
 * 
 * Accessible label component that automatically associates with form controls
 * and supports disabled states via peer selectors.
 * 
 * @component
 * @param {Object} props - Component props extending Radix Label.Root props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Label text content
 * @returns {JSX.Element} Rendered label element
 * 
 * @example
 * ```tsx
 * // Basic usage with input
 * <div>
 *   <Label htmlFor="email">Email Address</Label>
 *   <Input id="email" type="email" />
 * </div>
 * 
 * // With disabled input (label will automatically grey out)
 * <div>
 *   <Label htmlFor="disabled">Disabled Field</Label>
 *   <Input id="disabled" disabled />
 * </div>
 * 
 * // Custom styling
 * <Label htmlFor="name" className="text-lg font-bold">
 *   Full Name
 * </Label>
 * ```
 * 
 * @remarks
 * Features:
 * - Proper accessibility with htmlFor attribute
 * - Automatic styling for disabled inputs via peer selector
 * - Consistent typography and spacing
 * - Built on Radix UI for robust functionality
 */
const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
));
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
